import api from './api';
import { toQueryDateYMD } from '../utils/formatters';
import { corregirPrecargaProveedorConEmisor } from '../utils/compraFacturaPrecarga';
import { extraerEmisorFacturaCompraPdf } from '../utils/compraFacturaPdfEmisor';
import proveedorService from './proveedorService';
import { asPdfBlobOrThrow } from '../utils/apiError';

export interface FacturaCompraRemitoAsociado {
    nroFormulario: number;
    letra?: string;
    nroSuc: number;
    nroComprobante?: number;
}

export interface FacturaCompra {
    nroFormulario: number;
    fechaEmision: string | Date;
    fechaVencim?: string | Date;
    aplicacionIva?: string | Date;
    horaEmision?: string | Date;
    nroTipoFormulario?: string;
    tipoCliente?: string;
    tipoProveedor?: string;
    letra?: string;
    tipoIva?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nroProveedor?: number;
    nombre?: string;
    nroLista?: string;
    codigoMoneda?: number | string;
    subtotal?: number;
    descuento?: number;
    importeSinIva?: number;
    exento?: number;
    ivainscripto?: number;
    ivanoInscripto?: number;
    ivaotro?: number;
    importeTotal?: number;
    /** Asiento de libro diario vinculado a este formulario, si existe */
    nroAsiento?: number | null;
    asentado?: string;
    descripcion?: string;
    usuario?: string;
    cuit?: string;
    nroUbicacionDestino?: number;
    ubicacionDestinoDescripcion?: string;
    formaDePago?: string;
    formaEntrega?: string;
    ordenDeCompra?: string;
    porcDesc?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    percepciones?: number;
    impuestos?: ImpuestoPorFormularioReadDto[];
    remitosAsociados?: FacturaCompraRemitoAsociado[];
    detalles?: FacturaCompraDetalle[];
}

export interface ImpuestoPorFormularioReadDto {
    codigoImpuesto: string;
    montoBaseImp?: number;
    montoImpuesto?: number;
    ctaCtable?: number;
}

export interface FacturaCompraDetalle {
    nroIdformulario: number;
    nroItem: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioLista?: number;
    porcDesc?: number;
    precioUnitario?: number;
    descuento?: number;
    subtotal?: number;
    tasa1?: number;
    tasa2?: number;
    tasa3?: number;
    tasa4?: number;
    tasa5?: number;
    impInterno1?: number;
    impInterno2?: number;
    tasaHidrica?: number;
    notas?: string;
    tipoProveedor?: string;
    nroCuenta?: number;
}

export interface FacturaCompraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    nroProveedor?: string;
    asentado?: string;
    usuario?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nombreProveedor?: string;
    /** Si true, solo facturas (tipo 1). Por defecto incluye facturas y notas de débito (1 y 2). */
    soloFacturas?: boolean;
}

export interface FacturaCompraPagedResult {
    items: FacturaCompra[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Item del Libro IVA Compras (respuesta del endpoint ComprobantesCompra/libro-iva-compras) */
export interface LibroIvaComprasItem {
    nroformulario: number;
    fechaemision?: string;
    nroTipoFormulario?: string;
    documento?: string;
    letraSucNum?: string;
    nombre?: string;
    tipoIva?: string;
    cuit?: string;
    netoGrav?: number;
    exento?: number;
    iva10?: number;
    iva21?: number;
    iva27?: number;
    retIva?: number;
    aplicacionIva?: string;
    perIibb?: number;
    perIva?: number;
    impInt?: number;
    impMun?: number;
    impNac?: number;
    importeTotal?: number;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
}

/** Resultado paginado del Libro IVA Compras */
export interface LibroIvaComprasPagedResult {
    items: LibroIvaComprasItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalNetoGrav?: number;
    totalExento?: number;
    totalIva10?: number;
    totalIva21?: number;
    totalIva27?: number;
    totalRetIva?: number;
    totalPerIibb?: number;
    totalPerIva?: number;
    totalImpInt?: number;
    totalImpMun?: number;
    totalImpNac?: number;
    totalImporteTotal?: number;
}

export interface LibroIvaComprasArcaTxtResult {
    comprobantesFileName: string;
    comprobantesContent: string;
    alicuotasFileName: string;
    alicuotasContent: string;
}

/** Parámetros de búsqueda para libro IVA compras (endpoint distinto) */
export interface LibroIvaFacturaCompraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    nroProveedor?: number;
    nroSuc?: number;
    nombreProveedor?: string;
    idCompania?: string;
}

export interface FacturaCompraFormularioDto {
    idCompania: string;
    letra: string;
    nroSuc: number;
    nroComprobante: number;
    tipoProveedor: string;
    tipoComprobante: string;
    descripcion?: string;
    fechaEmision?: string;
    fechaVencim?: string;
    aplicacionIva?: string;
    nroProveedor: number;
    formaDePago?: string;
    nroUbicacionDestino?: number;
    porcDesc?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    /** Total de IVA de cabecera; si se envía, el API reparte en columnas sin modificar tasas de ítems. */
    ivaManual?: number;
}

export interface CreateItemCompraDto {
    codigoArticulo: string;
    descripcion?: string;
    cantidad: number;
    costoBrutoUnitario: number;
    porcDesc1?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    porcDesc4?: number;
    porcDesc5?: number;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
    notas?: string;
    tasa1?: number;
    tasa2?: number;
    tasa3?: number;
    tasa4?: number;
    tasa5?: number;
    impInterno1?: number;
    impInterno2?: number;
    tasaHidrica?: number;
    /** Tipo de proveedor del item: C (productos) o G (conceptos) */
    tipoProveedor?: 'C' | 'G';
    /** Número de cuenta del imputable. Obligatorio para items tipo G */
    nroCuenta?: number;
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    /** Si se informa, reemplaza el subtotal calculado (Cantidad × costo con desc. de ítem). */
    subtotalManual?: number;
}

/** Impuesto adicional del comprobante (percepciones, retenciones) */
export interface ImpuestoPorFormularioCreateDto {
    codigoImpuesto: string;
    ctaCtable: number;
    montoBaseImp?: number;
    montoImpuesto?: number;
}

export interface FacturaCompraCreatePayload {
    formulario: FacturaCompraFormularioDto;
    items: CreateItemCompraDto[];
    impuestos?: ImpuestoPorFormularioCreateDto[];
}

export interface FacturaCompraPrecargaCampo {
    nombre: string;
    valor?: string | null;
    confianza: number;
    fuente: string;
}

export interface FacturaCompraPrecargaProveedor {
    nroProveedor?: number | null;
    nombre?: string | null;
    cuit?: string | null;
    domicilio?: string | null;
    encontrado: boolean;
}

export interface FacturaCompraPrecargaFormulario {
    letra?: string | null;
    nroSuc?: number | null;
    nroComprobante?: number | null;
    tipoProveedor?: string | null;
    tipoComprobante?: string | null;
    descripcion?: string | null;
    fechaEmision?: string | null;
    fechaVencim?: string | null;
    aplicacionIva?: string | null;
    nroProveedor?: number | null;
    formaDePago?: string | null;
}

export interface FacturaCompraPrecargaDesdePdfResult {
    formulario?: FacturaCompraPrecargaFormulario | null;
    proveedor: FacturaCompraPrecargaProveedor;
    esNotaDebito: boolean;
    confianzaGlobal: number;
    campos: FacturaCompraPrecargaCampo[];
    advertencias: string[];
    itemsProductos?: FacturaCompraPrecargaItem[];
    itemsConceptos?: FacturaCompraPrecargaItem[];
}

export interface FacturaCompraPrecargaItem {
    tipoProveedor: 'C' | 'G';
    codigoArticulo: string;
    descripcion?: string | null;
    cantidad: number;
    costoBrutoUnitario: number;
    nroCuenta?: number | null;
    codigoArticuloProveedor?: string | null;
    notas?: string | null;
    advertencia?: string | null;
    porcentajeIva?: number | null;
    confianza?: number;
}

function tipoFormularioEsperado(esNotaDebito?: boolean): string {
    return esNotaDebito ? '2' : '1';
}

function coincideComprobante(
    fc: FacturaCompra,
    letra: string,
    nroSuc: number,
    nroComprobante: number,
    tipoEsperado: string
): boolean {
    const L = (fc.letra ?? '').trim().toUpperCase();
    const suc = Number(fc.nroSuc ?? 0);
    const nro = Number(fc.nroComprobante ?? 0);
    const tipo = String(fc.nroTipoFormulario ?? '1').trim() || '1';
    return L === letra && suc === nroSuc && nro === nroComprobante && tipo === tipoEsperado;
}

const facturaCompraService = {
    getFacturasCompra: async (
        params: FacturaCompraSearchParams,
        signal?: AbortSignal
    ): Promise<FacturaCompraPagedResult> => {
        const queryParams: Record<string, string | number | boolean | undefined> = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            searchTerm: params.searchTerm || undefined,
            nroProveedor: params.nroProveedor || undefined,
            asentado: params.asentado || undefined,
            usuario: params.usuario || undefined,
            letra: params.letra || undefined,
            nroSuc: params.nroSuc ?? undefined,
            nroComprobante: params.nroComprobante ?? undefined,
            soloFacturas: params.soloFacturas === true ? true : undefined
        };
        if (params.fechaDesde) {
            queryParams.fechaDesde = toQueryDateYMD(params.fechaDesde);
        }
        if (params.fechaHasta) {
            queryParams.fechaHasta = toQueryDateYMD(params.fechaHasta);
        }
        const response = await api.get<FacturaCompraPagedResult>('/api/FacturasCompra', {
            params: queryParams,
            signal
        });
        return response.data;
    },

    /**
     * Busca si ya existe un comprobante con la misma letra + sucursal + nro (y tipo FC/ND).
     * Sin filtro de fechas, para no perder coincidencias de otros meses.
     */
    buscarDuplicadoPorComprobante: async (params: {
        letra: string;
        nroSuc: number;
        nroComprobante: number;
        esNotaDebito?: boolean;
        signal?: AbortSignal;
    }): Promise<FacturaCompra | null> => {
        const letra = (params.letra ?? '').trim().toUpperCase();
        if (!letra || params.nroComprobante <= 0) return null;

        const result = await facturaCompraService.getFacturasCompra(
            {
                pageNumber: 1,
                pageSize: 10,
                letra,
                nroSuc: params.nroSuc,
                nroComprobante: params.nroComprobante
            },
            params.signal
        );

        const tipoEsperado = tipoFormularioEsperado(params.esNotaDebito);
        const match = (result.items || []).find((fc) =>
            coincideComprobante(fc, letra, params.nroSuc, params.nroComprobante, tipoEsperado)
        );
        return match ?? null;
    },

    getFacturaCompra: async (id: number): Promise<FacturaCompra> => {
        const response = await api.get<FacturaCompra>(`/api/FacturasCompra/${id}`);
        return response.data;
    },

    /** Solo SUPERADMIN: borra asientos vinculados al formulario y crea uno nuevo con la lógica actual. */
    regenerarAsientoFacturaCompra: async (
        id: number
    ): Promise<{ nroAsiento: number; message?: string }> => {
        const response = await api.post<{ nroAsiento: number; message?: string }>(
            `/api/FacturasCompra/${id}/regenerar-asiento`
        );
        return response.data;
    },

    createFacturaCompra: async (payload: FacturaCompraCreatePayload): Promise<FacturaCompra> => {
        const response = await api.post<FacturaCompra>('/api/FacturasCompra', payload);
        return response.data;
    },

    getEtiquetasCodigosPdf: async (
        nroFormulario: number,
        items: Array<{ codigoArticulo: string; copias: number }>,
    ): Promise<Blob> => {
        const response = await api.post(
            `/api/FacturasCompra/${nroFormulario}/etiquetas-codigos-pdf`,
            { items },
            { responseType: 'blob' },
        );
        return asPdfBlobOrThrow(response.data, 'El servidor no pudo generar las etiquetas');
    },

    getLibroIvaCompras: async (params: LibroIvaFacturaCompraSearchParams): Promise<LibroIvaComprasPagedResult> => {
        const queryParams: Record<string, string | number | undefined> = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };
        if (params.fechaDesde) {
            queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
        }
        if (params.nroProveedor) {
            queryParams.NroProveedor = params.nroProveedor;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreProveedor) {
            queryParams.NombreProveedor = params.nombreProveedor;
        }
        if (params.idCompania) {
            queryParams.IdCompania = params.idCompania;
        }
        const response = await api.get<LibroIvaComprasPagedResult>('/api/ComprobantesCompra/libro-iva-compras', {
            params: queryParams
        });
        return response.data;
    },

    exportLibroIvaComprasArcaTxt: async (params: {
        fechaDesde: Date | string;
        fechaHasta: Date | string;
        idCompania?: string;
    }): Promise<LibroIvaComprasArcaTxtResult> => {
        const response = await api.get<LibroIvaComprasArcaTxtResult>('/api/ComprobantesCompra/libro-iva-compras/arca-txt', {
            params: {
                fechaDesde: toQueryDateYMD(params.fechaDesde),
                fechaHasta: toQueryDateYMD(params.fechaHasta),
                idCompania: params.idCompania || undefined,
            },
        });
        return response.data;
    },

    precargarDesdePdf: async (archivo: File): Promise<FacturaCompraPrecargaDesdePdfResult> => {
        const form = new FormData();
        form.append('archivo', archivo);
        const [response, emisor] = await Promise.all([
            api.post<FacturaCompraPrecargaDesdePdfResult>(
                '/api/FacturasCompra/precarga-desde-pdf',
                form,
                {
                    transformRequest: [(body, headers) => {
                        delete headers['Content-Type'];
                        return body as FormData;
                    }],
                }
            ),
            extraerEmisorFacturaCompraPdf(archivo).catch(() => null),
        ]);

        let result = response.data;
        if (emisor && (emisor.cuit || emisor.nombre || emisor.domicilio)) {
            const { precarga, corregido } = corregirPrecargaProveedorConEmisor(result, emisor);
            result = precarga;

            if (corregido && emisor.cuit) {
                const digits = emisor.cuit.replace(/\D/g, '');
                try {
                    const search = await proveedorService.getProveedores({
                        searchTerm: digits,
                        pageNumber: 1,
                        pageSize: 20,
                    });
                    const match = search.items.find(
                        (p) => (p.cuit ?? '').replace(/\D/g, '') === digits,
                    );
                    if (match) {
                        result = {
                            ...result,
                            proveedor: {
                                ...result.proveedor,
                                encontrado: true,
                                nroProveedor: match.nroProveedor,
                                nombre: match.nombre ?? result.proveedor.nombre,
                                cuit: match.cuit ?? result.proveedor.cuit,
                            },
                        };
                    }
                } catch {
                    /* sin match en maestro */
                }
            }
        }

        return result;
    },

    puedeAnularFacturaCompra: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
        const response = await api.get<{ puedeAnular: boolean; motivos: string[] }>(
            `/api/FacturasCompra/${id}/puede-anular`
        );
        return response.data;
    },

    anularFacturaCompra: async (id: number, dto?: { motivo?: string }): Promise<void> => {
        await api.post(`/api/FacturasCompra/${id}/anular`, dto ?? {});
    }
};

export default facturaCompraService;

